import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction, Reducer } from '@reduxjs/toolkit';

import { ACCEPTANCE_ADVANCED_SIGNING_PROCESS_NAME, DEFAULT_ACCEPTANCE_ADVANCED_ID, initialState } from './consts';
import { AcceptedAdvancedSigningProcessState } from './types';

export const acceptanceAdvancedSlice = createSlice({
  name: ACCEPTANCE_ADVANCED_SIGNING_PROCESS_NAME,
  initialState,
  reducers: {
    acceptanceAdvancedSigningLoadingChanged: (
      state,
      action: PayloadAction<{ acceptanceAdvancedId?: string; loading?: boolean }>
    ) => {
      const { acceptanceAdvancedId = DEFAULT_ACCEPTANCE_ADVANCED_ID, loading = false } = action.payload;

      if (state.acceptancesAdvanced[acceptanceAdvancedId]) {
        state.acceptancesAdvanced[acceptanceAdvancedId].loading = loading;
        state.acceptancesAdvanced[acceptanceAdvancedId].lastUpdated = Date.now();
      }
    },

    startAcceptanceAdvancedSigning: (
      state,
      action: PayloadAction<{
        acceptanceAdvancedId?: string;
        companyId: string;
        uriForReturn: string;
        fallbackFn: () => void;
        preOpenedTab?: Window | null;
      }>
    ) => {
      const { acceptanceAdvancedId = DEFAULT_ACCEPTANCE_ADVANCED_ID } = action.payload;
      const existing = state.acceptancesAdvanced[acceptanceAdvancedId];

      state.acceptancesAdvanced[acceptanceAdvancedId] = {
        acceptanceAdvancedId,
        acceptanceAdvancedTabOpened: existing?.acceptanceAdvancedTabOpened,
        acceptanceAdvancedTabUrl: existing?.acceptanceAdvancedTabUrl,
        loading: true,
        lastUpdated: Date.now(),
      };
    },

    acceptanceAdvancedStatusUpdated: (
      state,
      action: PayloadAction<{
        acceptanceAdvancedId?: string;
        error?: string;
      }>
    ) => {
      const { acceptanceAdvancedId = DEFAULT_ACCEPTANCE_ADVANCED_ID, error } = action.payload;

      if (state.acceptancesAdvanced[acceptanceAdvancedId]) {
        state.acceptancesAdvanced[acceptanceAdvancedId].error = error;
        state.acceptancesAdvanced[acceptanceAdvancedId].loading = false;
        state.acceptancesAdvanced[acceptanceAdvancedId].lastUpdated = Date.now();
      }
    },

    setAcceptanceAdvancedTabUrl: (state, action: PayloadAction<{ acceptanceAdvancedId?: string; url: string }>) => {
      const { acceptanceAdvancedId = DEFAULT_ACCEPTANCE_ADVANCED_ID, url } = action.payload;

      if (state.acceptancesAdvanced[acceptanceAdvancedId]) {
        state.acceptancesAdvanced[acceptanceAdvancedId].acceptanceAdvancedTabUrl = url;
        state.acceptancesAdvanced[acceptanceAdvancedId].lastUpdated = Date.now();
      }
    },

    setAcceptanceAdvancedTab: (
      _,
      action: PayloadAction<{
        acceptanceAdvancedId?: string;
        openedWindow: Window | null;
        companyId: string;
      }>
    ) => {
      console.log(`Saving acceptance advanced tab for acceptance advanced signing process: ${action.payload}`);
    },

    focusAcceptanceAdvancedTab: (_, action: PayloadAction<string>) => {
      console.log(`Focusing acceptance advanced tab for acceptance advanced signing process: ${action.payload}`);
    },

    setAcceptanceAdvancedTabOpened: (state, action: PayloadAction<{ acceptanceAdvancedId?: string }>) => {
      const { acceptanceAdvancedId = DEFAULT_ACCEPTANCE_ADVANCED_ID } = action.payload;

      if (state.acceptancesAdvanced[acceptanceAdvancedId]) {
        state.acceptancesAdvanced[acceptanceAdvancedId].acceptanceAdvancedTabOpened = true;
        state.acceptancesAdvanced[acceptanceAdvancedId].lastUpdated = Date.now();
      }
    },

    clearAcceptanceAdvancedTabOpened: (state, action: PayloadAction<{ acceptanceAdvancedId?: string }>) => {
      const { acceptanceAdvancedId = DEFAULT_ACCEPTANCE_ADVANCED_ID } = action.payload;

      if (state.acceptancesAdvanced[acceptanceAdvancedId]) {
        state.acceptancesAdvanced[acceptanceAdvancedId].acceptanceAdvancedTabOpened = false;
        state.acceptancesAdvanced[acceptanceAdvancedId].lastUpdated = Date.now();
      }
    },

    stopAcceptanceAdvancedProcess: (
      state,
      action: PayloadAction<{ acceptanceAdvancedId?: string; clearData?: boolean }>
    ) => {
      const { acceptanceAdvancedId = DEFAULT_ACCEPTANCE_ADVANCED_ID, clearData = false } = action.payload;

      if (state.acceptancesAdvanced[acceptanceAdvancedId]) {
        state.acceptancesAdvanced[acceptanceAdvancedId].loading = false;

        if (clearData) {
          delete state.acceptancesAdvanced[acceptanceAdvancedId];
        }
      }
    },

    retryAcceptanceAdvanced: (state, action: PayloadAction<{ acceptanceAdvancedId?: string }>) => {
      const { acceptanceAdvancedId = DEFAULT_ACCEPTANCE_ADVANCED_ID } = action.payload;

      if (state.acceptancesAdvanced[acceptanceAdvancedId]) {
        delete state.acceptancesAdvanced[acceptanceAdvancedId].error;
        state.acceptancesAdvanced[acceptanceAdvancedId].loading = true;
        state.acceptancesAdvanced[acceptanceAdvancedId].lastUpdated = Date.now();
      }
    },

    clearAllAcceptanceAdvancedProcesses: (state) => {
      state.acceptancesAdvanced = {
        [DEFAULT_ACCEPTANCE_ADVANCED_ID]: {
          acceptanceAdvancedId: DEFAULT_ACCEPTANCE_ADVANCED_ID,
          loading: false,
          lastUpdated: Date.now(),
        },
      };
    },
  },
});

export const {
  acceptanceAdvancedSigningLoadingChanged,
  startAcceptanceAdvancedSigning,
  acceptanceAdvancedStatusUpdated,
  setAcceptanceAdvancedTab,
  setAcceptanceAdvancedTabUrl,
  focusAcceptanceAdvancedTab,
  setAcceptanceAdvancedTabOpened,
  clearAcceptanceAdvancedTabOpened,
  stopAcceptanceAdvancedProcess,
  retryAcceptanceAdvanced,
  clearAllAcceptanceAdvancedProcesses,
} = acceptanceAdvancedSlice.actions;

export const acceptanceAdvancedReducer: Reducer<AcceptedAdvancedSigningProcessState> = acceptanceAdvancedSlice.reducer;
